import { ListView, ListViewItem, type ListViewItemProps } from "@repo/skapa";
import { t } from "@repo/translation";
import { isChecked } from "@repo/utils";
import { useState } from "react";

export interface SelectListItem extends Pick<
  ListViewItemProps,
  "title" | "description" | "addOn" | "disabled" | "leadingIcon" | "image"
> {
  value: string;
  name?: string;
}

export type ListSelectionMode = "SINGLE" | "MULTI" | "NONE";

export interface SelectListViewProps {
  id: string;
  list: SelectListItem[];
  onChange: (
    checked: boolean,
    item: SelectListItem,
    selectedKeys: string[]
  ) => void;
  selectionMode?: ListSelectionMode;
  selectedKeys: string[];
  disabledKeys?: string[];
  selectAll?: boolean;
}

const controlMap: Record<
  ListSelectionMode,
  ListViewItemProps["control"] | undefined
> = {
  SINGLE: "radiobutton",
  MULTI: "checkbox",
  NONE: "default",
};

export const SelectListView = ({
  id,
  list,
  onChange,
  selectionMode = "MULTI",
  selectedKeys,
  disabledKeys,
  selectAll,
}: SelectListViewProps) => {
  const SelectAllItem: SelectListItem = {
    value: "SelectAll",
    title: t("action.select_all"),
  };

  const defaultValue =
    selectedKeys.length > 0 && selectionMode === "SINGLE"
      ? [selectedKeys[0]]
      : selectedKeys;

  const [selectedItems, setSelectedItems] = useState(defaultValue);

  const allItems = list.map((el: SelectListItem) => el.value);

  const handleSelectAll = (checked: boolean, item: SelectListItem) => {
    const values = checked ? allItems : [];

    setSelectedItems(values);
    onChange(checked, item, values);
  };

  const handleChange = (checked: boolean, item: SelectListItem) => {
    let values: string[];

    if (selectionMode === "SINGLE") {
      values = checked ? [item.value] : [];
    } else {
      values = checked
        ? [...selectedItems, item.value]
        : selectedItems.filter((selected) => selected !== item.value);
    }

    setSelectedItems(values);
    onChange(checked, item, values);
  };

  return (
    <ListView id={id}>
      {selectAll && selectionMode !== "SINGLE" && (
        <ListViewItem
          title={SelectAllItem.title}
          control="checkbox"
          emphasised
          controlProps={{
            value: SelectAllItem.value,
            checked: selectedItems.length === allItems.length,
            onChange: (e) => {
              handleSelectAll(e.target.checked, SelectAllItem);
            },
          }}
        />
      )}

      {list.map((item) => (
        <ListViewItem
          key={`${id}-${item.value}`}
          title={item.title}
          description={item.description}
          addOn={item.addOn}
          leadingIcon={item.leadingIcon}
          image={item.image}
          control={controlMap[selectionMode]}
          disabled={item.disabled}
          controlProps={{
            value: item.value,
            checked: isChecked(selectedItems, item.value),
            disabled: isChecked(disabledKeys ?? [], item.value),
            onChange: (e) => {
              handleChange(e.target.checked, item);
            },
          }}
        />
      ))}
    </ListView>
  );
};
